import { Observable } from 'rxjs/internal/Observable';
import { map, bufferTime, filter, distinctUntilChanged } from 'rxjs/operators';
import { type EventEmitter } from 'events';
import { type NetworkScores, EventType } from 'webrtc-issue-detector';
import { type NetworkQuality } from '@doxyme/ui/components';

/**
 * Represents processed network quality scores with both raw values and their corresponding quality levels.
 *
 * @property inbound - The processed inbound network score (0-5 scale, rounded to 1 decimal)
 * @property outbound - The processed outbound network score (0-5 scale, rounded to 1 decimal)
 * @property inboundNetworkQuality - The quality level derived from the inbound score
 * @property outboundNetworkQuality - The quality level derived from the outbound score
 */
export type ProcessedNetworkScores = {
  inbound: number | undefined;
  outbound: number | undefined;
  inboundNetworkQuality: NetworkQuality | undefined;
  outboundNetworkQuality: NetworkQuality | undefined;
};

/**
 * Converts a numeric network score into a NetworkQuality enum value.
 *
 * @param value - The network score to format (0-5 scale)
 * @returns The corresponding network quality level:
 *   - undefined if input is undefined or negative
 *   - Low if < 2
 *   - Medium if >= 2 and < 3
 *   - High if >= 3
 **/
export const getFormattedNetworkQuality = (
  value: number | undefined,
): NetworkQuality | undefined => {
  if (value === undefined || value < 0) {
    return undefined;
  }

  if (value < 2.5) {
    return 'low';
  } else if (value < 3.7) {
    return 'medium';
  }

  return 'high';
};

/**
 * Creates an Observable that processes and emits network quality scores.
 *
 * This function creates an observable pipeline that:
 * 1. Listens to NetworkScoresUpdated events from the provided EventEmitter
 * 2. Buffers events for the specified interval
 * 3. Processes and formats the network scores:
 *    - Rounds values to 1 decimal place
 *    - Converts raw scores to NetworkQuality levels
 * 4. Filters out updates where both inbound and outbound are undefined
 * 5. Deduplicates consecutive identical values
 **/
export const createNetworkScoresObservable = (
  eventEmitter: EventEmitter,
  bufferInterval: number,
): Observable<ProcessedNetworkScores> => {
  const networkScoresObservable = new Observable<NetworkScores>((observer) => {
    const listener = (scores: NetworkScores): void => {
      observer.next(scores);
    };
    eventEmitter.on(EventType.NetworkScoresUpdated, listener);
    return () => {
      eventEmitter.off(EventType.NetworkScoresUpdated, listener);
    };
  });

  return networkScoresObservable.pipe(
    bufferTime(bufferInterval),
    map((bufferedScores): ProcessedNetworkScores => {
      const inbound = { total: 0, count: 0 };
      const outbound = { total: 0, count: 0 };

      for (const score of bufferedScores) {
        if (score.inbound !== undefined) {
          inbound.total += score.inbound;
          inbound.count += 1;
        }
        if (score.outbound !== undefined) {
          outbound.total += score.outbound;
          outbound.count += 1;
        }
      }

      const inboundAverage =
        inbound.count !== 0
          ? Math.round((inbound.total / inbound.count) * 10) / 10
          : undefined;
      const outboundAverage =
        outbound.count !== 0
          ? Math.round((outbound.total / outbound.count) * 10) / 10
          : undefined;

      return {
        inbound: inboundAverage,
        outbound: outboundAverage,
        inboundNetworkQuality: getFormattedNetworkQuality(inboundAverage),
        outboundNetworkQuality: getFormattedNetworkQuality(outboundAverage),
      };
    }),

    filter((val) => val.inbound !== undefined || val.outbound !== undefined),
    distinctUntilChanged(
      (prev, curr) =>
        prev.inbound === curr.inbound && prev.outbound === curr.outbound,
    ),
  );
};
